import { Box, Button, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import TeamsService from 'api/services/TeamsService';
import CardMy from 'components/CardMy';
import InputMy from 'components/InputMy';
import Loader from 'components/Loader';
import TeamCardHeader from 'components/TeamCardHeader';
import _ from 'lodash';
import showMessage from 'providers/NoticeProvider/showMessage';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'routes';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchSingleTeam, selectSingleTeam, selectSingleTeamsIsLoading } from 'store/slices/teams';
import { selectMe } from 'store/slices/user';
import { TVariables } from 'store/types';

const SingleTeamChangeVariables = () => {
    const { slug } = useParams();
    const { company } = useAppSelector(selectMe);
    const { title, variables } = useAppSelector(selectSingleTeam);
    const navigate = useNavigate();
    const singleTeamsIsLoading = useAppSelector(selectSingleTeamsIsLoading);
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [values, setValues] = useState<TVariables>(variables);

    useEffect(() => {
        dispatch(fetchSingleTeam({ id: slug }))
            .unwrap()
            .catch(error => {
                console.log('error = ', error);
                navigate(ROUTES.team.fullPath);
            });
    }, []);

    useEffect(() => {
        setValues(variables);
    }, [variables]);

    const handleCancel = () => setValues(variables);

    const handleSave = async () => {
        try {
            setLoading(true);
            await TeamsService.updateVariables({
                companyId: company.id,
                teamId: slug,
                ...values,
            })
                .then(() => {
                    showMessage('Changes have been successfully saved.');
                    dispatch(fetchSingleTeam({ id: slug }));
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            return false;
        }
    };

    const disabled = _.isEqual(variables, values);

    return (
        <CardMy>
            {singleTeamsIsLoading ? (
                <Loader />
            ) : (
                <>
                    <TeamCardHeader backHref={ROUTES.team.fullPath} teamName={title} />
                    <Box borderTop="1px" borderColor="gray.100" marginX="-24px" padding="24px">
                        <Text color="gray.900" fontSize="20px" fontWeight={700} lineHeight={1} letterSpacing="-0.4px">
                            Variable Customization
                        </Text>
                        <Grid paddingY="24px" gap="16px" templateColumns="repeat(auto-fit, minmax(200px, 1fr))">
                            {_.map(variables, (value: number, key: keyof TVariables) => {
                                const handleChange = (e: ChangeEvent<HTMLInputElement>) =>
                                    setValues({ ...values, [key]: e.target.value });
                                return (
                                    <GridItem key={key}>
                                        <InputMy
                                            type="number"
                                            label={key}
                                            placeholder={'Enter value'}
                                            value={values?.[key] || 0}
                                            onChange={handleChange}
                                        />
                                    </GridItem>
                                );
                            })}
                        </Grid>
                        <Flex gap="8px" justifyContent="flex-end">
                            <Button
                                height="48px"
                                padding="0 24px"
                                variant="outline"
                                isDisabled={disabled || loading}
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                height="48px"
                                padding="0 24px"
                                variant="brand"
                                isDisabled={disabled}
                                onClick={handleSave}
                                isLoading={loading}
                            >
                                Update & Save
                            </Button>
                        </Flex>
                    </Box>
                </>
            )}
        </CardMy>
    );
};

export default SingleTeamChangeVariables;
